'use client';

import { ReactNode, useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * Default query client configuration for healthcare booking workflows
 * - Extended timeouts for complex backend operations
 * - Disabled automatic retries for mutations to prevent duplicate bookings
 */
const createQueryClientConfig = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        retry: 3,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // Disable automatic retries for mutations to prevent duplicate bookings
        retry: false,
        // Network timeout handled by fetch AbortController in createMutation
      },
    },
  });

export function QueryProvider({ children }: QueryProviderProps): JSX.Element {
  // Ensure a new QueryClient is created per component mount
  const [queryClient] = useState(createQueryClientConfig);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
